import React from 'react';
import Axios from 'axios';
import classNames from 'classnames';
import { flatMapDeep, flatMap, isEmpty, differenceBy, map, noop, snakeCase } from 'lodash';
import { useQuery } from 'react-query';
import { components } from 'react-select';
import { ComboBox } from '@fiverr-private/orca';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger } from '@fiverr-private/obs';
import withQueryClient from './withQueryClient';
import { GET_INDUSTRIES_URL } from './constants';

import styles from './IndustriesSelect.module.scss';
import './IndustriesSelect.scss';

class IndustryNode {
    /**
     * @param {object} constructor
     * @param {string} constructor.id
     * @param {string} constructor.slug
     * @param {string} constructor.title
     * @param {Array<IndustryNode | {id: string}>} [constructor.children]
     * @param {Array<string>} [constructor.aliases]
     * @param {string} [constructor.parentId]
     */
    constructor({ id, slug, title, children, aliases, parentId }) {
        this.id = id;
        this.slug = slug;
        this.title = title;

        if (children) {
            this.children = children;
        }

        if (aliases) {
            this.aliases = aliases;
        }

        if (parentId) {
            this.parentId = parentId;
        }
    }
}

const transformIndustryTree = (industry, parentId) => {
    const { id, fields } = industry;
    const { slug, title, aliases, children } = fields;

    return new IndustryNode({
        id,
        slug,
        title,
        parentId,
        aliases,
        children: children && children.map((child) => transformIndustryTree(child, title !== 'Root' ? id : null))
    });
};

const getChildren = (list) => flatMap(list, (item) => item.children || []);

const mapToOption = (node, fallbackValue) => {
    if (!node && fallbackValue) return {
        label: fallbackValue,
        value: snakeCase(fallbackValue)
    };

    return { label: node.title, value: node.slug };
};

const findIndustriesByTerm = (list, term) => {
    const compare = (word) => word.startsWith(term.toLowerCase());

    const getWords = (word) => word.toLowerCase().split(' ');

    return list.filter((item) => {
        // Check if term has any white space in it
        if (/\s/.test(term)) {
            const re = new RegExp(term, 'i');
            if (re.test(item.title)) return true;

            if (!isEmpty(item.aliases)) {
                return item.aliases.some((alias) => re.test(alias));
            }
        } else {
            if (getWords(item.title).some(compare)) return true;

            if (!isEmpty(item.aliases)) {
                return item.aliases.some((alias) => getWords(alias).some(compare));
            }
        }

        return false;
    });
};

const getIndustries = async (locale) => {
    const mapChildrenToReference = (list) => {
        if (!list) return [];

        return list.map((item) => {
            const result = { id: item.id };

            if (!isEmpty(item.children)) {
                result.children = mapChildrenToReference(item.children);
            }

            return result;
        });
    };

    const unpackList = (list) => {
        return flatMapDeep(list, (item) => {
            const result = [item];

            if (!isEmpty(item.children)) {
                result.push(unpackList(item.children));
            }

            return result;
        });
    };

    const { data } = await Axios.get(GET_INDUSTRIES_URL, {
        params: {
            locale
        }
    });

    const root = transformIndustryTree(data);

    const list = unpackList(root.children);

    return list.map((item) => ( { ...item, children: mapChildrenToReference(item.children) } ));
};

/**
 * @param {import('axios').AxiosError} error
 * @returns {void}
 */
const handleFetchError = (error) => logger.error('Failed to load industries list', error);

const MAX_SELECTED_INDUSTRIES = 2;

const InputComponent = (props) => {
    const cantAddOptions = props.getValue().length >= MAX_SELECTED_INDUSTRIES;

    return <components.Input {...props} isHidden={cantAddOptions}/>;
};

/** @type {import('react').FC<import('../../types').IndustriesSelectProps>} */
const IndustriesSelect = ({
    name,
    value,
    onChange = noop,
    onBlur = noop,
    hasError = false,
    id = 'industries-select-package-input',
    isLoading = false,
    placeholder,
    label,
    withLabel,
    withNote,
    controlClassName = '',
    isInPortal = true,
    className = '',
    isMulti = true,
    menuShouldBlockScroll = true,
    isDisabled = false,
    menuPlacement = ComboBox.MENU_PLACEMENTS.AUTO,
    shouldAcceptCustomValues = false
}) => {
    const { locale } = getContext();
    const [internalValue, setInternalValue] = React.useState([]);
    const [menuIsOpen, setMenuIsOpen] = React.useState(false);

    const { data: industries, isLoading: getIndustriesIsLoading, refetch, isIdle } = useQuery(
        'get-industries',
        () => getIndustries(locale),
        {
            staleTime: Infinity,
            enabled: Array.isArray(value) ? !isEmpty(value) : !!value,
            onError: handleFetchError
        }
    );

    const loadOptions = async (term) => {
        const directMatch = findIndustriesByTerm(industries, term);
        const parents = industries.filter(({ id }) => map(directMatch, 'parentId').includes(id));
        const children = industries.filter(({ id }) => map(getChildren(directMatch), 'id').includes(id));
        const recommendations = differenceBy([...parents, ...children], directMatch, 'id');

        return !isEmpty(recommendations)
            ? [
                ...directMatch.map(mapToOption),
                {
                    label: i18n.t('industries_select.related_industries', { $scope: locale }),
                    options: recommendations.map(mapToOption)
                }
            ]
            : directMatch.map(mapToOption);
    };

    const getValue = () => {
        if (!value || !industries || isEmpty(value)) return internalValue;
        const fallbackValue = shouldAcceptCustomValues ? value : undefined;

        if (!isMulti) return mapToOption(industries.find(({
            slug,
            title
        }) => slug === value || title === value), fallbackValue);

        return industries.filter(({ slug }) => value.includes(slug)).map(foundIndustry => mapToOption(foundIndustry, fallbackValue));
    };

    const onMenuOpen = () => {
        if (isIdle) refetch();
    };

    const internalOnChange = (options) => {
        if (!options) {
            setInternalValue([]);
            onChange([]);
            return;
        }

        if (!isMulti || options.length <= MAX_SELECTED_INDUSTRIES) {
            setInternalValue(options);
            onChange(options);
        }
    };

    const handleInputChange = (input) => {
        if (input.length > 0 && !menuIsOpen && ( internalValue.length < MAX_SELECTED_INDUSTRIES || !isMulti )) {
            setMenuIsOpen(true);
        }

        if (!input && menuIsOpen) {
            setMenuIsOpen(false);
        }
    };

    const notSearchable =
        isMulti &&
        ( value && value.length >= MAX_SELECTED_INDUSTRIES ) ||
        ( !value && internalValue.length >= MAX_SELECTED_INDUSTRIES );

    React.useEffect(() => {
        if (notSearchable) {
            document.getElementById(id).setAttribute('readonly', 'true');
        } else {
            document.getElementById(id).removeAttribute('readonly');
        }
    }, [id, notSearchable]);

    const props = {
        isMulti,
        isAsync: true,
        backspaceRemovesValue: true,
        placeholder: placeholder || i18n.t('industries_select.placeholder', { $scope: locale }),
        name,
        id,
        isLoading: getIndustriesIsLoading || isLoading,
        value: getValue(),
        onChange: internalOnChange,
        onBlur,
        hasError,
        loadOptions,
        defaultOptions: industries ? industries.map(mapToOption) : [],
        onMenuOpen,
        menuIsOpen,
        ChevronComponent: null,
        InputComponent,
        onInputChange: handleInputChange,
        menuShouldBlockScroll,
        className: classNames(className, { 'not-searchable': notSearchable }),
        isInPortal,
        isDisabled,
        menuPlacement
    };

    return (
        <div className={controlClassName}>
            {withLabel && (
                <div className="m-b-8">
                    <label htmlFor={id} className={styles.label}>
                        {label || i18n.t('industries_select.label', { $scope: locale })}
                    </label>
                </div>
            )}
            <ComboBox {...props} />
            {withNote && (
                <div className="m-t-8 tbody-6">
                    <small className={styles.note}>{i18n.t('industries_select.note', { $scope: locale })}</small>
                </div>
            )}
        </div>
    );
};

export default withQueryClient(IndustriesSelect);
